import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8081';

const BusinessDuserProfile = () => {
  const [profileImage, setProfileImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [activeTab, setActiveTab] = useState("profileDetails");
  const [existingImagePath, setExistingImagePath] = useState(null);
  const [loading, setLoading] = useState(true);

  const [businessDUser, setBusinessDuser] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    emailAddress: "",
    profileImage: "",
    CompanyCode: "",
    role: "",
    accountType: "",
    country: ""
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');

  const businessDUserData = JSON.parse(localStorage.getItem("dUser"));
  const businessDUserId = businessDUserData?.id;

  const notify = (message, type = 'success') => toast[type](message, {
    position: "top-right",
    autoClose: 3000
  });

  useEffect(() => {
    const fetchBusinessDUserData = async () => {
      if (!businessDUserId) {
        notify("Invalid business department user ID provided.", "error");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log("Fetching data for business department user ID:", businessDUserId);
        
        const response = await fetch(`${BASE_URL}/get-business-department-user/${businessDUserId}`);
        console.log("Response status:", response.status);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log("Raw API response:", data);
        
        const userData = data.data || data.user || data;
        console.log("Processed user data:", userData);
        
        setBusinessDuser({
          firstName: userData.firstName || "",
          lastName: userData.lastName || "",
          phoneNumber: userData.phoneNumber || "",
          emailAddress: userData.emailAddress || userData.email || "",
          profileImage: userData.profileImage || "",
          CompanyCode: userData.CompanyCode || userData.companyCode || "",
          role: userData.role || "",
          accountType: userData.accountType || "",
          country: userData.country || ""
        });
        
        if (userData.profileImage) {
          const imageUrl = userData.profileImage.startsWith('http') 
            ? userData.profileImage 
            : `${BASE_URL}${userData.profileImage}`;
          setPreviewImage(imageUrl);
          setExistingImagePath(userData.profileImage);
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error loading business department user data:", error);
        notify("Failed to load business department user data.", "error");
        setLoading(false);
      }
    };

    fetchBusinessDUserData();
  }, [businessDUserId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBusinessDuser(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    
    if (passwordError) {
      setPasswordError('');
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    if (!businessDUser.firstName || !businessDUser.lastName) {
      notify("First name and last name are required.", "error");
      return;
    }

    try {
      if (profileImage && existingImagePath) {
        try {
          const deleteResponse = await fetch(`${BASE_URL}/delete-business-department-user-profile-image`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              dUserId: businessDUserId,
              imagePath: existingImagePath
            })
          });
          
          if (!deleteResponse.ok) {
            console.warn('Failed to delete old profile image, but continuing with update');
          }
        } catch (deleteError) {
          console.warn('Error deleting old profile image:', deleteError);
        }
      }

      const formData = new FormData();
      formData.append("firstName", businessDUser.firstName);
      formData.append("lastName", businessDUser.lastName);
      formData.append("phoneNumber", businessDUser.phoneNumber);
      formData.append("country", businessDUser.country);
      
      if (profileImage) {
        formData.append("profileImage", profileImage);
      }

      const response = await fetch(`${BASE_URL}/update-business-department-user/${businessDUserId}`, {
        method: 'PUT',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update profile');
      }

      const data = await response.json();
      
      setBusinessDuser(prev => ({
        ...prev,
        firstName: data.data.firstName,
        lastName: data.data.lastName,
        phoneNumber: data.data.phoneNumber,
        profileImage: data.data.profileImage
      }));

      if (data.data.profileImage) {
        setPreviewImage(`${BASE_URL}${data.data.profileImage}`);
        setExistingImagePath(data.data.profileImage);
      }

      const updatedBusinessDUserData = {
        ...businessDUserData,
        ...data.data
      };
      localStorage.setItem('businessDUser', JSON.stringify(updatedBusinessDUserData));

      setProfileImage(null);
      const fileInput = document.getElementById('profileImage');
      if (fileInput) fileInput.value = '';

      notify("Profile updated successfully!");
    } catch (error) {
      console.error("Error updating profile:", error);
      notify(error.message || "Error updating profile. Please try again.", "error");
    }
  };

  const handlePasswordUpdate = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordData;

    if (!currentPassword || !newPassword || !confirmPassword) {
      notify('All fields are required', "error");
      return;
    }

    if (newPassword !== confirmPassword) {
      notify('New passwords do not match', "error");
      return;
    }

    try {
      const verifyResponse = await fetch(`${BASE_URL}/business-department-user-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          emailAddress: businessDUser.emailAddress,
          password: currentPassword
        })
      });

      if (!verifyResponse.ok) {
        notify('Current password is incorrect', "error");
        return;
      }

      const formData = new FormData();
      formData.append("password", newPassword);

      const response = await fetch(`${BASE_URL}/update-business-department-user/${businessDUserId}`, {
        method: 'PUT',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update password');
      }

      notify('Password updated successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setPasswordError('');
    } catch (error) {
      console.error("Error updating password:", error);
      notify(error.message || 'Failed to update password', "error");
    }
  };

  return (
    <div className="container-fluid" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      <ToastContainer />
      <div className="row justify-content-center py-5">
        <div className="col-12 col-md-10">
          <div className="card shadow-sm">
            <div className="card-header bg-white py-3">
              <h4 className="mb-0 text-primary">Business Department User Profile</h4>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-5">
                <div className="spinner-grow text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="text-primary fw-bold mb-1">Loading business user profile</p>
                <p className="text-muted">Please wait while we fetch business user profile data...</p>
              </div>
              ) : (
                <div className="row">
                  <div className="col-md-3 text-center mb-4">
                    <div className="position-relative d-inline-block">
                      <img
                        src={
                          previewImage || 
                          (businessDUser.profileImage ? (businessDUser.profileImage.startsWith('http') ? businessDUser.profileImage : `${BASE_URL}${businessDUser.profileImage}`) : "https://www.w3schools.com/w3images/avatar2.png")
                        }
                        alt="Profile"
                        className="rounded-circle mb-3"
                        style={{
                          width: "150px",
                          height: "150px",
                          objectFit: "cover",
                          border: "3px solid #007bff"
                        }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "https://www.w3schools.com/w3images/avatar2.png";
                        }}
                      />
                      <label
                        htmlFor="profileImage"
                        className="btn btn-sm btn-primary position-absolute bottom-0 end-0"
                        style={{ borderRadius: "50%", padding: "8px" }}
                      >
                        <i className="fas fa-camera"></i>
                      </label>
                      <input
                        type="file"
                        id="profileImage"
                        className="d-none"
                        onChange={handleImageChange}
                        accept="image/*"
                      />
                    </div>
                    <h5 className="mt-3">{businessDUser.firstName} {businessDUser.lastName}</h5>
                    <p className="text-muted small">Business Department User</p>
                  </div>

                  <div className="col-md-9">
                    <ul className="nav nav-tabs mb-4">
                      <li className="nav-item">
                        <button
                          className={`nav-link ${activeTab === "profileDetails" ? "active" : ""}`}
                          onClick={() => setActiveTab("profileDetails")}
                        >
                          Profile Details
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          className={`nav-link ${activeTab === "changePassword" ? "active" : ""}`}
                          onClick={() => setActiveTab("changePassword")}
                        >
                          Security
                        </button>
                      </li>
                    </ul>

                    {activeTab === "profileDetails" && (
                      <form>
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label className="form-label">First Name</label>
                            <input
                              type="text"
                              name="firstName"
                              className="form-control"
                              value={businessDUser.firstName}
                              onChange={handleInputChange}
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Last Name</label>
                            <input
                              type="text"
                              name="lastName"
                              className="form-control"
                              value={businessDUser.lastName}
                              onChange={handleInputChange}
                            />
                          </div>
                        </div>

                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label className="form-label">Email Address</label>
                            <input
                              type="email"
                              className="form-control"
                              value={businessDUser.emailAddress}
                              disabled
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Phone Number</label>
                            <input
                              type="text"
                              name="phoneNumber"
                              className="form-control"
                              value={businessDUser.phoneNumber || ''}
                              onChange={handleInputChange}
                            />
                          </div>
                        </div>

                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label className="form-label">Country</label>
                            <input
                              type="text"
                              name="country"
                              className="form-control"
                              value={businessDUser.country || ''}
                              onChange={handleInputChange}
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Company Code</label>
                            <input
                              type="text"
                              className="form-control"
                              value={businessDUser.CompanyCode || ''}
                              disabled
                            />
                          </div>
                        </div>

                        <div className="d-flex justify-content-end">
                          <button
                            type="button"
                            className="btn btn-primary px-4"
                            onClick={handleSave}
                          >
                            Save Changes
                          </button>
                        </div>
                      </form>
                    )}

                    {activeTab === "changePassword" && (
                      <form>
                        {passwordError && (
                          <div className="alert alert-danger">{passwordError}</div>
                        )}
                        <div className="row mb-3">
                          <div className="col-md-12">
                            <label className="form-label">Current Password</label>
                            <input
                              type="password"
                              name="currentPassword"
                              className="form-control"
                              value={passwordData.currentPassword}
                              onChange={handlePasswordChange}
                            />
                          </div>
                        </div>
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label className="form-label">New Password</label>
                            <input
                              type="password"
                              name="newPassword"
                              className="form-control"
                              value={passwordData.newPassword}
                              onChange={handlePasswordChange}
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Confirm New Password</label>
                            <input
                              type="password"
                              name="confirmPassword"
                              className="form-control"
                              value={passwordData.confirmPassword}
                              onChange={handlePasswordChange}
                            />
                          </div>
                        </div>
                        <div className="d-flex justify-content-end">
                          <button
                            type="button"
                            className="btn btn-primary px-4"
                            onClick={handlePasswordUpdate}
                          >
                            Update Password
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessDuserProfile;